import { matchPath } from 'react-router-dom';

import { RBAC_ENABLED } from 'constants/rbacConfig';

/**
 * Which permission each screen needs — the single table both portals gate on.
 *
 * ── Why one table and not a `permission:` key on each nav item ───────────────
 *
 * The sidebar and the router are two different files with two different shapes
 * (`navigation.js` groups items; `routes.jsx` lists paths), and the invariant
 * that matters is that they agree: an item hidden from the nav must also refuse a
 * typed URL, or hiding it is decoration. Keying both off ONE table keyed by PATH
 * makes disagreement unrepresentable. Annotating the nav config instead would
 * leave the router ungated, which is the state this file exists to end.
 *
 * ── Matching ────────────────────────────────────────────────────────────────
 *
 * Keys are route PATTERNS, matched with react-router's own `matchPath`, so
 * `/admin/bookings/:bookingId` covers `/admin/bookings/ZVY-2025-0031` and nothing
 * else. Reusing the router's matcher rather than a prefix test is deliberate:
 * a hand-rolled `startsWith` makes `/admin` a prefix of every admin screen, so a
 * page nobody remembered to map would silently inherit the Dashboard's
 * requirement instead of being flagged as unmapped.
 *
 * ── The values ──────────────────────────────────────────────────────────────
 *
 *   { side, module, subModule? }   the module a viewer needs SOME grant under
 *   PUBLIC                         deliberately ungated (see below)
 *
 * `side` and `module` must match the backend's catalogue strings character for
 * character, spaces and `&` included — they are compared as raw substrings of a
 * permission code. The catalogue lives in
 * app-backend/src/utils/constants/rolePermissions.ts (MODULE_ORDER /
 * SUB_MODULE_ORDER) and the codes are `${side}:${module}[/${subModule}]:${action}`.
 * A typo here does not throw; it silently matches nothing, and the screen
 * disappears for everyone. If you add a module, add it in both places.
 *
 * Visibility asks only "is ANY action granted under this module" — see
 * `canAccessModule` in helpers/permissions.js. Which ACTIONS a viewer holds is a
 * separate question, asked per-button by the pages themselves.
 */

/** A screen that is intentionally reachable by anyone signed in. */
export const PUBLIC = 'PUBLIC';

/**
 * Platform admin portal (`/admin/*`) — every route in admin/config/routes.jsx.
 *
 * Kept in the same order as that file so the two can be diffed by eye; the
 * `assertNavPermissionCoverage` check below is what actually enforces that
 * neither grows an entry the other lacks.
 */
export const ADMIN_PERMISSIONS = {
  '/admin': { side: 'admin', module: 'Dashboard' },

  // The embedded traveller-app surfaces. `crm:Travel/*` did not exist in the
  // catalogue until migrations/nav_permission_catalogue.sql — if these three
  // vanish for everyone, that seed has not been run on this environment.
  '/admin/travel/chat': { side: 'crm', module: 'Travel', subModule: 'Start Chat' },
  '/admin/travel/shop': { side: 'crm', module: 'Travel', subModule: 'Book' },
  '/admin/travel/history': { side: 'crm', module: 'Travel', subModule: 'Chat History' },

  // ⚠️ The screen is called Partners and the route says `tenants`, but the
  // permission module is "Partner Management". Three names, one thing.
  '/admin/tenants': { side: 'admin', module: 'Partner Management' },
  '/admin/branding': { side: 'admin', module: 'Branding & Theming' },

  '/admin/service-fees': { side: 'admin', module: 'Revenue', subModule: 'Service Fees' },
  '/admin/markup-rules': { side: 'admin', module: 'Revenue', subModule: 'Markup Rules' },
  '/admin/deal-codes': { side: 'admin', module: 'Revenue', subModule: 'Deal Codes' },
  // ⚠️ NOT Deal Codes. routePermissions.ts calls the two out as separate
  // features on purpose: a role granted Deal Codes must not thereby reach Dropnet.
  '/admin/dropnet': { side: 'admin', module: 'Revenue', subModule: 'Dropnet' },
  // TEMPORARY: back on Deal Codes until admin:Revenue/Discount is seeded and granted;
  // then switch to subModule: 'Discount' (APImdFiles/FRONTEND_DISCOUNT_API.md §10).
  '/admin/discounts': { side: 'admin', module: 'Revenue', subModule: 'Deal Codes' },

  // GST rates ride on the catalogue's existing `admin:Settings` module (view +
  // edit, minimum role PARTNER_ADMIN). No route mapped to it before this — the
  // sidebar group of the same name is only a label — so gating GST on it widens
  // nothing. Do NOT invent a 'GST' module: a string absent from app-backend's
  // MODULE_ORDER matches nothing and silently hides the screen for everyone once
  // RBAC is on with a non-empty list. Caveat: `admin:Settings` also gates the
  // /app/v1/beadmin/* monitoring readers, so a role granted it for GST holds that
  // API authority too (it already did — this only gives the grant a screen).
  // The page opens on any Settings action; Save requires `edit`.
  '/admin/gst': { side: 'admin', module: 'Settings' },

  '/admin/suppliers': { side: 'admin', module: 'Suppliers', subModule: 'Supplier Config' },
  '/admin/supplier-mapping': { side: 'admin', module: 'Suppliers', subModule: 'Partner Mapping' },
  // Gated as Supplier Config until app-backend's catalogue gets an Airlines
  // Config sub-module: an invented name would match nothing and hide the screen
  // for everyone once RBAC is on. Repoint here when the catalogue has it.
  '/admin/airlines-config': { side: 'admin', module: 'Suppliers', subModule: 'Supplier Config' },

  // Two tabs, two modules. The PAGE opens for either — locking someone out of
  // Users because they only administer Roles would be absurd — and each tab
  // gates itself. See UsersRolesPage.
  '/admin/users': [
    { side: 'admin', module: 'User Management' },
    { side: 'admin', module: 'Role Management' },
  ],
  '/admin/feature-flags': { side: 'admin', module: 'Feature Flags' },

  // Not a screen — a redirect onto the Tenants page, where approvals became a
  // tab. It carries the DESTINATION's requirement rather than Approval Queue's,
  // because landing a user on a page they cannot see is worse than a 403.
  '/admin/approvals': { side: 'admin', module: 'Partner Management' },

  '/admin/bookings': { side: 'crm', module: 'Bookings', subModule: 'Booking Management' },
  '/admin/bookings/manual-hotel': { side: 'crm', module: 'Bookings', subModule: 'Manual Booking' },
  '/admin/bookings/manual-flight': { side: 'crm', module: 'Bookings', subModule: 'Manual Booking' },
  // Must sit AFTER the two manual routes — `:bookingId` would otherwise swallow
  // them. Insertion order is the tiebreak; see `requirementForPath`.
  '/admin/bookings/:bookingId': { side: 'crm', module: 'Bookings', subModule: 'Booking Management' },

  '/admin/customers': { side: 'crm', module: 'Customer Management' },
  '/admin/customers/:customerId': { side: 'crm', module: 'Customer Management' },
  '/admin/servicing': { side: 'crm', module: 'Post-Booking Servicing' },
  '/admin/finance': { side: 'crm', module: 'Finance & Settlement' },
  '/admin/communications': { side: 'crm', module: 'Communication Hub' },
  // ⚠️ The catalogue carries BOTH "crm:Reports" and "crm:Reports & Analytics",
  // with the same two actions. "Reports & Analytics" is the one in MODULE_ORDER
  // and the one the role editor renders, so it is the real module; the bare
  // "Reports" rows look like an early draft nobody cleaned up. Gating on the
  // wrong one of the pair hides the screen from roles that hold the other, so
  // this asks for EITHER.
  '/admin/reports': [
    { side: 'crm', module: 'Reports & Analytics' },
    { side: 'crm', module: 'Reports' },
  ],
  // Your own account details — every signed-in user may see their own.
  '/admin/profile': PUBLIC,
};

/**
 * Partner admin portal (`/partnerAdmin/*`) — every route in
 * partner/config/routes.jsx, with the `/partnerAdmin` prefix its route table
 * omits.
 *
 * The partner portal has no Suppliers, Feature Flags, Approvals or Tenants, so
 * the admin-side modules it does use are the tenant-scoped ones: its own
 * branding, its own markup, its own users.
 */
export const PARTNER_PERMISSIONS = {
  '/partnerAdmin': { side: 'admin', module: 'Dashboard' },

  '/partnerAdmin/travel/chat': { side: 'crm', module: 'Travel', subModule: 'Start Chat' },
  '/partnerAdmin/travel/shop': { side: 'crm', module: 'Travel', subModule: 'Book' },
  '/partnerAdmin/travel/history': { side: 'crm', module: 'Travel', subModule: 'Chat History' },
  // No `Travel/Email Queue` sub-module in the backend catalogue yet. It rides on
  // Chat History (a bare `Travel` rule would show it to any Travel grant, Start
  // Chat-only roles included). Add the sub-module there and here before go-live.
  '/partnerAdmin/travel/emails': { side: 'crm', module: 'Travel', subModule: 'Chat History' },

  '/partnerAdmin/branding': { side: 'admin', module: 'Branding & Theming' },

  '/partnerAdmin/commercials/markup': { side: 'admin', module: 'Revenue', subModule: 'Markup Rules' },
  '/partnerAdmin/commercials/fees': { side: 'admin', module: 'Revenue', subModule: 'Service Fees' },
  '/partnerAdmin/commercials/promo-codes': { side: 'admin', module: 'Revenue', subModule: 'Promo Codes' },
  // The pre-wiring mock of the Promo Codes screen, kept for side-by-side
  // comparison and hidden from the sidebar. Same module as the real one — it
  // shows the same data class, so it should not outlive its permission.
  '/partnerAdmin/commercials/promos': { side: 'admin', module: 'Revenue', subModule: 'Promo Codes' },

  '/partnerAdmin/platform/users': [
    { side: 'admin', module: 'User Management' },
    { side: 'admin', module: 'Role Management' },
  ],
  // Route-only, no sidebar entry. Partner balances are money, so it answers to
  // Finance & Settlement rather than being left ungated because nothing links
  // to it — an unlinked route is still a typeable URL.
  '/partnerAdmin/platform/wallet': { side: 'crm', module: 'Finance & Settlement' },

  '/partnerAdmin/bookings': { side: 'crm', module: 'Bookings', subModule: 'Booking Management' },
  '/partnerAdmin/bookings/manual-hotel': { side: 'crm', module: 'Bookings', subModule: 'Manual Booking' },
  '/partnerAdmin/bookings/manual-flight': { side: 'crm', module: 'Bookings', subModule: 'Manual Booking' },
  '/partnerAdmin/bookings/:id': { side: 'crm', module: 'Bookings', subModule: 'Booking Management' },

  '/partnerAdmin/customers': { side: 'crm', module: 'Customer Management' },
  '/partnerAdmin/customers/:id': { side: 'crm', module: 'Customer Management' },
  '/partnerAdmin/servicing': { side: 'crm', module: 'Post-Booking Servicing' },
  '/partnerAdmin/finance/transactions': { side: 'crm', module: 'Finance & Settlement' },
  // Partner GST rates. NOT `admin:Settings` like the CRM's /admin/gst: the new
  // default tenant-admin role (PR_TENANT_ADMIN) holds exactly the modules the
  // partner portal has screens for and deliberately excludes Settings, so that
  // mapping would hide the page from every new partner. GST is a charge on
  // service fee + markup, and Service Fees is one of those modules (it already
  // gates /partnerAdmin/commercials/fees). Any action opens the page; Save
  // requires `edit`. app-backend must gate /app/v1/partners/gst/rates/* on it too.
  '/partnerAdmin/gst': { side: 'admin', module: 'Revenue', subModule: 'Service Fees' },
  '/partnerAdmin/communications': { side: 'crm', module: 'Communication Hub' },

  '/partnerAdmin/reports/sales': [
    { side: 'crm', module: 'Reports & Analytics' },
    { side: 'crm', module: 'Reports' },
  ],
  '/partnerAdmin/reports/agents': [
    { side: 'crm', module: 'Reports & Analytics' },
    { side: 'crm', module: 'Reports' },
  ],
  '/partnerAdmin/reports/suppliers': [
    { side: 'crm', module: 'Reports & Analytics' },
    { side: 'crm', module: 'Reports' },
  ],
  '/partnerAdmin/reports/transactions': [
    { side: 'crm', module: 'Reports & Analytics' },
    { side: 'crm', module: 'Reports' },
  ],
  // Your own account details — every signed-in user may see their own.
  '/partnerAdmin/profile': PUBLIC,
};

/** Both tables, so a caller that does not care which portal it is in can ask once. */
const TABLES = [ADMIN_PERMISSIONS, PARTNER_PERMISSIONS];

/**
 * The requirement for a concrete pathname, or `undefined` when nothing maps it.
 *
 * `undefined` and `PUBLIC` mean different things and callers must not conflate
 * them: `PUBLIC` is "decided, and the answer is everyone"; `undefined` is "nobody
 * decided". The gate treats an unmapped path as ungated — a screen that appears
 * for everyone is a visible bug somebody reports, whereas one that vanishes for
 * everyone looks like the feature working. `assertNavPermissionCoverage` is what
 * turns that quiet default into a loud one in development.
 *
 * More specific patterns win regardless of declaration order, so a table edit
 * cannot accidentally let `/admin/bookings/:bookingId` swallow
 * `/admin/bookings/manual-hotel`: a pattern with fewer dynamic segments, then
 * more static ones, sorts first.
 */
export const requirementForPath = (pathname) => {
  if (!pathname) return undefined;

  const candidates = [];
  for (const table of TABLES) {
    for (const pattern of Object.keys(table)) {
      if (matchPath({ path: pattern, end: true }, pathname)) {
        candidates.push({ pattern, requirement: table[pattern] });
      }
    }
  }
  if (!candidates.length) return undefined;
  if (candidates.length === 1) return candidates[0].requirement;

  const dynamicCount = (p) => (p.match(/:/g) || []).length;
  candidates.sort(
    (a, b) =>
      dynamicCount(a.pattern) - dynamicCount(b.pattern) ||
      b.pattern.split('/').length - a.pattern.split('/').length ||
      b.pattern.length - a.pattern.length,
  );
  return candidates[0].requirement;
};

/**
 * Can this permission list see the screen at `pathname`?
 *
 * Three ways to be allowed, and the first two are the ones that keep the portal
 * usable rather than correct-but-empty:
 *
 *   1. the list is EMPTY — not gated at all. A session minted before the login
 *      response carried permissions, or a role nobody has granted anything, must
 *      fall through to server enforcement rather than dead-lock the screen. This
 *      matches PromoCodesPage, which established the rule.
 *   2. the path is unmapped or PUBLIC — see `requirementForPath`.
 *   3. some action is granted under the required module. An array of
 *      requirements means ANY of them (tabs, or the Reports naming collision).
 */
export const canSeePath = (perms, pathname) => {
  // The master switch is checked HERE rather than only at the call sites, so a
  // future caller that forgets it cannot re-introduce gating in an environment
  // that has deliberately turned the whole system off.
  if (!RBAC_ENABLED) return true;
  if (!perms || !perms.list?.length) return true;

  const requirement = requirementForPath(pathname);
  if (!requirement || requirement === PUBLIC) return true;

  const options = Array.isArray(requirement) ? requirement : [requirement];
  return options.some(({ side, module, subModule }) =>
    subModule
      ? perms.canAccessModule(side, `${module}/${subModule}`)
      : perms.canAccessModule(side, module),
  );
};

/**
 * Shout, in development only, about screens no one mapped.
 *
 * The gate fails open on an unmapped path, which is the right runtime behaviour
 * and a terrible way to find out you forgot one — the screen simply keeps
 * working for everybody and looks gated. This is called once from each portal's
 * shell with that portal's route table, and turns the silence into a console
 * warning naming the exact paths.
 *
 * Deliberately a warning and not a thrown error: a missing mapping must never be
 * the reason an operator cannot open the portal.
 */
export const assertNavPermissionCoverage = (routePaths, table, portalLabel) => {
  if (process.env.NODE_ENV === 'production') return;

  const mapped = new Set(Object.keys(table));
  const unmapped = routePaths.filter((path) => !mapped.has(path));
  const stale = [...mapped].filter((pattern) => !routePaths.includes(pattern));

  if (unmapped.length) {
    console.warn(
      `[navPermissions] ${portalLabel}: ${unmapped.length} route(s) have no permission mapping and are ` +
        `visible to everyone. Add them to src/constants/navPermissions.js:\n  ${unmapped.join('\n  ')}`,
    );
  }
  if (stale.length) {
    console.warn(
      `[navPermissions] ${portalLabel}: ${stale.length} mapped path(s) no longer exist in the route table. ` +
        `Remove them from src/constants/navPermissions.js:\n  ${stale.join('\n  ')}`,
    );
  }
};
